import { Injectable } from '@angular/core'
import { timer, Subscription, Observable, map, Subject, throwError, firstValueFrom } from 'rxjs'
import { LocationService } from './location.service'
import { ChannelService } from './channel.service'
import { TimeFormatter } from '../utils/time-formatter'
import { generateUUID } from '../utils/uuid'
import { LogService } from 'htv-lib'
import { ALARM_CONFIG } from '../constants/alarm.constants'
import { HTVLocation, WakeUpAlarmConfig } from '../interfaces/location'
import { PlatformService } from '../platforms/platform.service'
import { PowerState } from '../interfaces/power-state'
import { StateService } from './state.service'
import { RouterService, RouterPage } from './router.service'

@Injectable({
  providedIn: 'root',
})
export class AlarmService {
  private checkAlarmSubscription?: Subscription
  private locationSubscription?: Subscription
  private midnightTimeoutId?: ReturnType<typeof setTimeout>
  private triggeredAlarmIds = new Set<string>()
  private isMonitoring = false
  private isRinging = false

  wakeupAlarmConfigs: WakeUpAlarmConfig[] = []
  lastTriggeredAlarm?: WakeUpAlarmConfig
  lastTriggeredAlarmTime?: string

  private ringingAlarmChangedSubject = new Subject<WakeUpAlarmConfig>()
  ringingAlarmChanged$ = this.ringingAlarmChangedSubject.asObservable()

  readonly MAX_ALARMS = 10

  get wakeupAlarmConfig(): WakeUpAlarmConfig | undefined {
    return this.wakeupAlarmConfigs[0]
  }

  constructor(
    private channelService: ChannelService,
    private locationService: LocationService,
    private logService: LogService,
    private platformService: PlatformService,
    private stateService: StateService,
    private routerService: RouterService
  ) {
    // do nothing
  }

  init(): void {
    this.logService.debug('[AlarmService] Initializing')

    this.setupLocationListener()
    this.resetTriggeredAlarmsAtMidnight()
  }

  private setupLocationListener(): void {
    this.locationSubscription = this.locationService.location$.subscribe(location => {
      this.wakeupAlarmConfigs = location?.wakeupAlarmConfigs || []

      if (this.wakeupAlarmConfigs.length > 0 && !this.isMonitoring) {
        this.startAlarmMonitoring()
      } else if (this.wakeupAlarmConfigs.length === 0 && this.isMonitoring) {
        this.stopAlarmMonitoring()
      }
    })
  }

  createAlarm(config: Omit<WakeUpAlarmConfig, 'id'>): Observable<WakeUpAlarmConfig[]> {
    if (this.wakeupAlarmConfigs.length >= this.MAX_ALARMS) {
      return throwError(() => new Error('Maximum number of alarms reached'))
    }

    const newAlarm: WakeUpAlarmConfig = {
      ...config,
      id: generateUUID(),
    }
    const updatedConfigs = [...this.wakeupAlarmConfigs, newAlarm]
    return this.locationService
      .updateWakeUpAlarmConfigs(updatedConfigs)
      .pipe(map((location: HTVLocation) => location.wakeupAlarmConfigs || []))
  }

  updateAlarm(alarmId: string, updates: Partial<WakeUpAlarmConfig>): Observable<WakeUpAlarmConfig[]> {
    if (updates.time) {
      this.clearTriggeredStateForAlarm(alarmId)
    }

    const updatedConfigs = this.wakeupAlarmConfigs.map(alarm =>
      alarm.id === alarmId ? { ...alarm, ...updates } : alarm
    )
    return this.locationService
      .updateWakeUpAlarmConfigs(updatedConfigs)
      .pipe(map((location: HTVLocation) => location.wakeupAlarmConfigs || []))
  }

  deleteAlarm(alarmId: string): Observable<WakeUpAlarmConfig[]> {
    const updatedConfigs = this.wakeupAlarmConfigs.filter(a => a.id !== alarmId)
    return this.locationService
      .updateWakeUpAlarmConfigs(updatedConfigs)
      .pipe(map((location: HTVLocation) => location.wakeupAlarmConfigs || []))
  }

  getAlarmById(alarmId: string): WakeUpAlarmConfig | undefined {
    return this.wakeupAlarmConfigs.find(a => a.id === alarmId)
  }

  snoozeAlarm(alarmId: string): Observable<WakeUpAlarmConfig[]> {
    const alarm = this.getAlarmById(alarmId)
    if (!alarm?.time) {
      return throwError(() => new Error('Alarm not found'))
    }

    const [hours, minutes] = alarm.time.split(':').map(Number)
    let newMinutes = minutes + 5
    let newHours = hours

    if (newMinutes >= 60) {
      newMinutes -= 60
      newHours = (newHours + 1) % 24
    }

    const newTime = TimeFormatter.formatTimeString(newHours, newMinutes)
    return this.updateAlarm(alarmId, { time: newTime })
  }

  updateWakeUpAlarmConfig(config?: WakeUpAlarmConfig): Observable<WakeUpAlarmConfig | undefined> {
    const configs = config ? [config] : []
    return this.locationService
      .updateWakeUpAlarmConfigs(configs)
      .pipe(map((location: HTVLocation) => location.wakeupAlarmConfigs?.[0]))
  }

  clearLastTriggeredAlarmTime(): void {
    this.lastTriggeredAlarmTime = undefined
    this.lastTriggeredAlarm = undefined
    this.isRinging = false
  }

  private clearTriggeredStateForAlarm(alarmId: string): void {
    const currentDateKey = this.getCurrentDateString()
    const alarmKey = `${alarmId}-${currentDateKey}`
    this.triggeredAlarmIds.delete(alarmKey)
    this.logService.debug(`[AlarmService] Cleared triggered state for alarm ${alarmId} on ${currentDateKey}`)
  }

  private startAlarmMonitoring(): void {
    this.checkAlarmSubscription?.unsubscribe()

    if (this.isMonitoring) {
      return
    }

    this.logService.debug('[AlarmService] Starting alarm monitoring')
    this.isMonitoring = true

    this.checkAlarmSubscription = timer(0, ALARM_CONFIG.INTERVALS.CHECK_ALARM_MS).subscribe(() => {
      this.checkAlarmTrigger()
    })
  }

  private stopAlarmMonitoring(): void {
    if (!this.isMonitoring) {
      return
    }

    this.logService.debug('[AlarmService] Stopping alarm monitoring')
    this.isMonitoring = false
    this.checkAlarmSubscription?.unsubscribe()
  }

  private async checkAlarmTrigger(): Promise<void> {
    if (this.wakeupAlarmConfigs.length === 0) {
      return
    }

    const currentTime = this.getCurrentTimeString()
    const currentDateKey = this.getCurrentDateString()

    const alarmsToTrigger = this.wakeupAlarmConfigs.filter(alarm => {
      if (!alarm.time || !alarm.id) return false
      const alarmKey = `${alarm.id}-${currentDateKey}`
      if (this.triggeredAlarmIds.has(alarmKey)) return false
      return this.shouldTriggerAlarm(currentTime, alarm.time)
    })

    if (alarmsToTrigger.length === 0) {
      return
    }

    const [alarmToTrigger, ...duplicateAlarms] = alarmsToTrigger

    for (const duplicate of duplicateAlarms) {
      if (!duplicate.id) continue
      this.logService.debug(`[AlarmService] Deleting duplicate alarm ${duplicate.id} at same time ${duplicate.time}`)
      try {
        await firstValueFrom(this.deleteAlarm(duplicate.id))
        const duplicateKey = `${duplicate.id}-${currentDateKey}`
        this.triggeredAlarmIds.add(duplicateKey)
      } catch (err) {
        this.logService.error(`Failed to delete duplicate alarm: ${err}`)
      }
    }

    const previousRingingAlarmId = this.isRinging ? this.lastTriggeredAlarm?.id : undefined
    if (previousRingingAlarmId) {
      this.logService.debug(
        `[AlarmService] Replacing ringing alarm ${previousRingingAlarmId} with new alarm ${alarmToTrigger.id}`
      )
      try {
        await firstValueFrom(this.deleteAlarm(previousRingingAlarmId))
      } catch (err) {
        this.logService.error(`[AlarmService] Failed to delete previous ringing alarm: ${err}`)
      }
    }

    try {
      this.lastTriggeredAlarm = alarmToTrigger
      this.lastTriggeredAlarmTime = alarmToTrigger.time
      this.isRinging = true

      const alarmKey = `${alarmToTrigger.id}-${currentDateKey}`
      this.triggeredAlarmIds.add(alarmKey)

      if (previousRingingAlarmId) {
        this.ringingAlarmChangedSubject.next(alarmToTrigger)
      } else {
        await this.triggerAlarm(alarmToTrigger)
      }
    } catch (err) {
      this.logService.error(`[AlarmService] Failed to trigger alarm: ${err}`)
      this.isRinging = false
    }
  }

  private getCurrentTimeString(): string {
    const now = new Date()
    return TimeFormatter.formatTimeString(now.getHours(), now.getMinutes())
  }

  private getCurrentDateString(): string {
    return new Date().toISOString().split('T')[0]
  }

  private shouldTriggerAlarm(currentTime: string, alarmTime: string): boolean {
    return currentTime === alarmTime
  }

  private async triggerAlarm(alarm: WakeUpAlarmConfig): Promise<void> {
    if (alarm.channelId) {
      await this.triggerChannelAlarm(alarm.channelId, alarm.id)
    } else {
      await this.triggerSoundAlarm()
    }
  }

  private async triggerChannelAlarm(channelId: string, alarmId?: string): Promise<void> {
    this.logService.debug(`[AlarmService] Alarm triggered! Loading channel: ${channelId}`)

    const channel = this.findChannelByGlobalId(channelId)

    if (!channel) {
      this.logService.error(`[AlarmService] Channel not found: ${channelId}`)
      await this.triggerSoundAlarm()
      return
    }

    try {
      this.stateService.disablePowerOnReloadFlag = true
      this.platformService.setPowerState(PowerState.NORMAL)
      this.channelService.openChannelPage(channel)

      if (alarmId) {
        await firstValueFrom(this.deleteAlarm(alarmId))
        this.logService.debug(`[AlarmService] Channel alarm ${alarmId} deleted after triggering`)
      }

      this.clearLastTriggeredAlarmTime()
    } catch (error) {
      this.logService.error(`[AlarmService] Failed to open channel page or delete alarm: ${error}`)
      await this.triggerSoundAlarm()
    }
  }

  private async triggerSoundAlarm(): Promise<void> {
    this.logService.debug('[AlarmService] Alarm triggered! Navigating to alarm-ringing page')
    try {
      this.stateService.disablePowerOnReloadFlag = true
      await this.routerService.goToPage(RouterPage.WAKE_UP_ALARM_RINGING)
      this.platformService.setPowerState(PowerState.NORMAL)
    } catch (error) {
      this.logService.error(`[AlarmService] Failed to navigate to alarm-ringing page: ${error}`)
    }
  }

  private findChannelByGlobalId(globalChannelId: string) {
    return this.channelService.channels.find(channel => channel.globalChannelId === globalChannelId)
  }

  private resetTriggeredAlarmsAtMidnight(): void {
    const now = new Date()
    const tomorrow = new Date(now)
    tomorrow.setDate(tomorrow.getDate() + 1)
    tomorrow.setHours(0, 0, 0, 0)
    const msUntilMidnight = tomorrow.getTime() - now.getTime()

    this.midnightTimeoutId = setTimeout(() => {
      this.triggeredAlarmIds.clear()
      this.resetTriggeredAlarmsAtMidnight()
    }, msUntilMidnight)
  }

  cleanup(): void {
    this.locationSubscription?.unsubscribe()
    if (this.midnightTimeoutId) {
      clearTimeout(this.midnightTimeoutId)
    }
    this.stopAlarmMonitoring()
  }
}
